'use client';

import React, { useMemo, useState } from 'react';
import { Building2, History, Users } from 'lucide-react';

import { ContactsView } from '@/components/call/ui/ContactsView';
import { CrmDialContactsList } from '@/components/call/ui/CrmDialContactsList';
import { RecentsView } from '@/components/call/ui/RecentsView';
import { cn } from '@/lib/utils';
import type { CallLog, CallType, OperationsUser } from '@/components/call/types';
import type {
  CallsContactsSort,
  CallsRecentsScope,
} from '@/components/total-connect-rtc/TotalConnectCallsListToolbar';

export type DialCompanionTab = 'team' | 'crm' | 'recents';

export interface TotalConnectIdleDialCompanionProps {
  isDark: boolean;
  primaryColor: string;
  showCrmTab: boolean;
  operationsUsers: OperationsUser[];
  loadingUsers: boolean;
  contactsSort: CallsContactsSort;
  isUserOnline: (user: OperationsUser) => boolean;
  getUserLastSeen: (user: OperationsUser) => string | null;
  currentUserId?: number;
  canAccessPhoneContacts: boolean;
  onOpenPhoneContacts: () => void;
  onStartTeamCall: (user: OperationsUser, type: CallType) => void;
  callLogs: CallLog[];
  loadingCallLogs: boolean;
  recentsScope: CallsRecentsScope;
  onRecentsScopeChange: (scope: CallsRecentsScope) => void;
  recentsTimeSort: 'newest' | 'oldest';
  onCallBack: (userId: number, callType: CallType) => void;
  /** PSTN/extension dial string into keypad */
  onDialIntoKeypad: (number: string) => void;
}

/**
 * Wide column beside keypad (embedded desktop suite): Team, CRM dial book, Recents —
 * distinct from vendor softphones but keeps dialing context actionable.
 */
export function TotalConnectIdleDialCompanion({
  isDark,
  primaryColor,
  showCrmTab,
  operationsUsers,
  loadingUsers,
  contactsSort,
  isUserOnline,
  getUserLastSeen,
  currentUserId,
  canAccessPhoneContacts,
  onOpenPhoneContacts,
  onStartTeamCall,
  callLogs,
  loadingCallLogs,
  recentsScope,
  onRecentsScopeChange,
  recentsTimeSort,
  onCallBack,
  onDialIntoKeypad,
}: TotalConnectIdleDialCompanionProps) {
  const [tab, setTab] = useState<DialCompanionTab>('team');
  const [filter, setFilter] = useState('');

  const effectiveTab: DialCompanionTab =
    tab === 'crm' && !showCrmTab ? 'team' : tab;

  const tabs = useMemo(() => {
    const base: { id: DialCompanionTab; label: string; icon: typeof Users }[] = [
      { id: 'team', label: 'Team', icon: Users },
      { id: 'recents', label: 'Recents', icon: History },
    ];
    if (showCrmTab) base.splice(1, 0, { id: 'crm', label: 'CRM', icon: Building2 });
    return base;
  }, [showCrmTab]);

  const scopeLabel = (s: CallsRecentsScope) =>
    s === 'sip' ? 'SIP/PSTN' : s === 'team' ? 'App' : 'All';

  return (
    <div
      className={cn(
        'flex h-full min-h-[240px] min-w-0 flex-col overflow-hidden border-l',
        isDark ? 'border-gray-800/80 bg-gray-950/40' : 'border-gray-200/70 bg-white',
      )}
    >
      <div
        className={cn(
          'flex shrink-0 flex-wrap items-center gap-1 border-b px-2 py-2',
          isDark ? 'border-gray-800 bg-gray-900/90' : 'border-gray-200 bg-gray-50/95',
        )}
        role="tablist"
        aria-label="Dial companion"
      >
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={effectiveTab === id}
            onClick={() => setTab(id)}
            className={cn(
              'inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition-colors sm:text-xs',
              effectiveTab === id
                ? isDark
                  ? 'bg-white/10 text-white ring-1 ring-white/15'
                  : 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                : isDark
                  ? 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                  : 'text-gray-600 hover:bg-white/70 hover:text-gray-900',
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            {label}
          </button>
        ))}
      </div>

      {effectiveTab === 'recents' ? (
        <div
          className={cn(
            'flex shrink-0 flex-wrap gap-1 border-b px-2 py-1.5',
            isDark ? 'border-gray-800' : 'border-gray-200',
          )}
        >
          {(['all', 'sip', 'team'] as CallsRecentsScope[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onRecentsScopeChange(s)}
              className={cn(
                'rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                recentsScope === s
                  ? isDark
                    ? 'bg-white/15 text-white'
                    : 'bg-gray-900 text-white'
                  : isDark
                    ? 'text-gray-500 hover:bg-white/5 hover:text-gray-300'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800',
              )}
            >
              {scopeLabel(s)}
            </button>
          ))}
        </div>
      ) : null}

      {effectiveTab === 'team' || effectiveTab === 'recents' ? (
        <div className={cn('shrink-0 border-b px-2 py-1.5', isDark ? 'border-gray-800' : 'border-gray-200')}>
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={effectiveTab === 'recents' ? 'Search calls…' : 'Search team…'}
            aria-label={
              effectiveTab === 'recents' ? 'Filter recent calls' : 'Filter team contacts'
            }
            className={cn(
              'w-full rounded-md border px-2 py-1.5 text-xs focus:border-transparent focus:outline-none focus:ring-1',
              isDark
                ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder:text-gray-500'
                : 'border-gray-200 bg-white text-gray-900 placeholder:text-gray-400',
            )}
            style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
          />
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {effectiveTab === 'team' ? (
          <ContactsView
            users={operationsUsers}
            loading={loadingUsers}
            searchQuery={filter}
            onSearchChange={setFilter}
            onStartCall={onStartTeamCall}
            isUserOnline={isUserOnline}
            getUserLastSeen={getUserLastSeen}
            isDark={isDark}
            primaryColor={primaryColor}
            currentUserId={currentUserId}
            canAccessPhoneContacts={canAccessPhoneContacts}
            onOpenPhoneContacts={onOpenPhoneContacts}
            hideSearchBar
            sortMode={contactsSort}
          />
        ) : effectiveTab === 'crm' ? (
          <div className="min-h-[200px] p-2">
            <CrmDialContactsList
              isDark={isDark}
              primaryColor={primaryColor}
              onSelectPhone={(raw) => onDialIntoKeypad(raw)}
              onOpenContact={() => {
                window.open('/client-zone/modules/total-crm?tab=contacts', '_blank', 'noopener,noreferrer');
              }}
              nameSort={contactsSort === 'name_desc' ? 'desc' : 'asc'}
            />
          </div>
        ) : effectiveTab === 'recents' ? (
          <RecentsView
            callLogs={callLogs}
            loading={loadingCallLogs}
            searchQuery={filter}
            onSearchChange={setFilter}
            onCallBack={onCallBack}
            onDialNumber={onDialIntoKeypad}
            currentUserId={currentUserId}
            isDark={isDark}
            primaryColor={primaryColor}
            recentsScope={recentsScope}
            onRecentsScopeChange={onRecentsScopeChange}
            recentsTimeSort={recentsTimeSort}
            hideRecentsToolbar
          />
        ) : null}
      </div>
    </div>
  );
}
